import { Button, Dialog } from '@noorden-insurtech/alba-design-system';
import { Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import type { PolicyBatchUploadResponse } from '../../../services/integrations/types';

interface UploadBatchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (file: File) => void;
  isLoading: boolean;
  result?: { success: boolean; data?: PolicyBatchUploadResponse; error?: string } | null;
}

export function UploadBatchModal({
  isOpen,
  onClose,
  onUpload,
  isLoading,
  result,
}: UploadBatchModalProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setSelectedFile(file);
  };

  const handleSubmit = () => {
    if (selectedFile) {
      onUpload(selectedFile);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    onClose();
  };

  const uploadData = result?.data;

  return (
    <Dialog.Root open={isOpen} onOpenChange={(details) => {
      if (!details.open) handleClose();
    }}>
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content className='w-[520px] max-w-[90vw]'>
          <Dialog.Header>
            <Dialog.Title>Importar Balde de Apólices</Dialog.Title>
          </Dialog.Header>

          <Dialog.Body className='space-y-4'>
            <p className='text-sm text-alba-gray-600'>
              Selecione um arquivo Excel (.xlsx) com os números de apólice para importar.
            </p>

            <p className='text-xs text-alba-gray-500'>
              Colunas obrigatórias: <span className='font-medium'>ID Solicitação</span>,{' '}
              <span className='font-medium'>Nº Ramo</span>,{' '}
              <span className='font-medium'>Id Origem Emissão</span>,{' '}
              <span className='font-medium'>Nº Balde</span>,{' '}
              <span className='font-medium'>Data Balde</span> e{' '}
              <span className='font-medium'>Nº Apólice</span>.
            </p>

            <div className='flex items-center gap-3'>
              <input
                ref={fileInputRef}
                type='file'
                accept='.xlsx'
                onChange={handleFileChange}
                className='text-sm text-alba-gray-700 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border file:border-alba-gray-300 file:bg-white file:text-sm file:font-medium file:text-alba-gray-700 hover:file:bg-alba-gray-50'
              />
            </div>

            {result && !isLoading && (
              <div className={`rounded-lg p-4 text-sm ${result.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                {result.success && uploadData ? (
                  <div className='space-y-1'>
                    <p className='font-medium text-green-800'>Importação concluída</p>
                    <p className='text-green-700'>Total de linhas: {uploadData.totalRows}</p>
                    <p className='text-green-700'>Inseridos: {uploadData.inserted}</p>
                    {uploadData.failed > 0 && (
                      <p className='text-red-600'>Falhas: {uploadData.failed}</p>
                    )}
                    {uploadData.errors && uploadData.errors.length > 0 && (
                      <div className='mt-2 max-h-[150px] overflow-auto'>
                        {uploadData.errors.slice(0, 10).map((err, i) => (
                          <p key={i} className='text-red-600 text-xs'>
                            Linha {err.rowNumber}: {err.message}
                          </p>
                        ))}
                        {uploadData.errors.length > 10 && (
                          <p className='text-red-500 text-xs mt-1'>
                            ... e mais {uploadData.errors.length - 10} erros
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <p className='text-red-800'>{result.error || 'Erro ao importar arquivo'}</p>
                )}
              </div>
            )}
          </Dialog.Body>

          <Dialog.Footer>
            <Button variant='ghost' onClick={handleClose}>
              {result?.success ? 'Fechar' : 'Cancelar'}
            </Button>
            {!result?.success && (
              <Button
                variant='primary'
                onClick={handleSubmit}
                disabled={!selectedFile || isLoading}
              >
                <Upload className='w-4 h-4 mr-1' />
                {isLoading ? 'Importando...' : 'Importar'}
              </Button>
            )}
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
}
